import { Box, Button, TextField, Typography } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { userApi } from "api/user/user";
import { useFormik } from "formik";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { snackBarStyle } from "styles/snackBarStyle";
import { textFieldStyles } from "styles/textFieldStyles";
import * as Yup from "yup";

const ChangePasswordPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: userApi.updatePassword,
    mutationKey: "users",
    onSuccess: (response) => {
      if (response.status === 200) {
        enqueueSnackbar("Adgangskode blev ændret!", snackBarStyle());
        navigate("/profile");
        formik.resetForm();
      } else {
        enqueueSnackbar(
          "Adgangskode skal minimum være 8 karakterer. Adgangskode skal indeholde et lille bogstav. Adgangskode skal indeholde et stort bogstav. Adgangskode skal indeholde et tal. Adgangskode skal indeholde et symbol.",
          snackBarStyle("error", 3000)
        );
      }
    },
    onError: (error) => {
      enqueueSnackbar(
        "Adgangskode skal minimum være 8 karakterer. Adgangskode skal indeholde et lille bogstav. Adgangskode skal indeholde et stort bogstav. Adgangskode skal indeholde et tal. Adgangskode skal indeholde et symbol.",
        snackBarStyle("error", 3000)
      );
    },
  });

  const validationSchema = Yup.object().shape({
    old_password: Yup.string().required("Gammel adgangskode er påkrævet"),
    new_password: Yup.string()
      .required("New password is required")
      .min(8, "Ny adgangskode er påkrævet")
      .matches(/[A-Z]/, "Adgangskoden skal indeholde mindst ét ​​stort bogstav")
      .matches(/[a-z]/, "Adgangskoden skal indeholde mindst ét ​​lille bogstav")
      .matches(/\d/, "Adgangskoden skal indeholde mindst ét ​​tal")
      .matches(
        /[!@#$%^&*()_+{}\[\]:;"'<>,.?~\\/-]/,
        "Adgangskoden skal indeholde mindst ét ​​symbol"
      ),
    confirm_password: Yup.string()
      .oneOf([Yup.ref("new_password"), null], "Adgangskoder skal matche")
      .required("Bekræft adgangskode er påkrævet"),
  });

  const formik = useFormik({
    initialValues: {
      old_password: "",
      new_password: "",
      confirm_password: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values, { resetForm }) => {
      mutation.mutate(values);
    },
  });

  return (
    <Box
      component="main"
      sx={{
        height: "100vh",
        width: 1,
        position: "fixed",
        top: 0,
        left: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        component={"div"}
        sx={{
          height: "100vh",
          width: 1,
          position: "fixed",
          top: 0,
          left: 0,
          bgcolor: "black",
          zIndex: -10,
          opacity: "0",
        }}
      ></Box>
      <Box
        component={"div"}
        sx={{
          bgcolor: "white",
          p: { xs: 3, sm: 4 },
          width: { xs: "80%", sm: "440px" },
          maxWidth: "440px",
          boxShadow: 1,
        }}
      >
        <Typography variant="h6" component="h2" mb={3} textAlign={"center"}>
          Skift din adgangskode for at sikre
        </Typography>
        <Box
          component="form"
          noValidate
          onSubmit={formik.handleSubmit}
          sx={{
            mt: 1,
            width: 1,
          }}
        >
          <Box mb={2} position={"relative"}>
            <TextField
              required
              fullWidth
              id="old_password"
              label="Gammelt passord"
              name="old_password"
              autoComplete="old_password"
              type="password"
              value={formik.values.old_password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.old_password &&
                Boolean(formik.errors.old_password)
              }
              sx={textFieldStyles}
            />
            {formik.touched.old_password &&
              Boolean(formik.errors.old_password) && (
                <Typography
                  component={"p"}
                  mt={0.5}
                  position={"absolut"}
                  fontSize={12}
                  color={"error"}
                >
                  {formik.errors.old_password}
                </Typography>
              )}
          </Box>
          <Box mb={2} position={"relative"}>
            <TextField
              required
              fullWidth
              id="new_password"
              label="Nyt kodeord"
              name="new_password"
              autoComplete="new_password"
              type="password"
              value={formik.values.new_password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.new_password &&
                Boolean(formik.errors.new_password)
              }
              sx={{
                ...textFieldStyles,
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "#b7ab90",
                  },
                },
                "& .MuiInputLabel-root": {
                  "&.Mui-focused": {
                    color: "#b7ab90",
                  },
                },
              }}
            />
            {formik.touched.new_password &&
              Boolean(formik.errors.new_password) && (
                <Typography
                  component={"p"}
                  mt={0.5}
                  position={"absolut"}
                  fontSize={12}
                  color={"error"}
                >
                  {formik.errors.new_password}
                </Typography>
              )}
          </Box>
          <Box mb={2} position={"relative"}>
            <TextField
              required
              fullWidth
              id="confirm_password"
              label="Bekræft kodeord"
              name="confirm_password"
              autoComplete="confirm_password"
              type="password"
              value={formik.values.confirm_password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.confirm_password &&
                Boolean(formik.errors.confirm_password)
              }
              sx={textFieldStyles}
            />
            {formik.touched.confirm_password &&
              Boolean(formik.errors.confirm_password) && (
                <Typography
                  position={"absolute"}
                  component={"p"}
                  mt={0.5}
                  fontSize={12}
                  color={"error"}
                >
                  {formik.errors.confirm_password}
                </Typography>
              )}
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              mt: 2,
              mb: 2,
              height: "50px",
              bgcolor: "#424242",
              "&:hover": {
                bgcolor: "#686868",
              },
            }}
          >
            {mutation.isLoading ? "Laoding..." : "Lave om"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
export default ChangePasswordPage;
